// Starting a fade on an AudioParam without a click.

import { CURVE_POINTS, curve, gainAt, type Fade } from './fade.ts';
import type { ParamLike } from './webaudio.ts';

// Short fades need fewer ramp points than a full-length one: this many per second, within bounds.
const POINTS_PER_S = 20;
const MIN_POINTS = 4;

const DB_PER_DECADE = 20;

/** A fade holding `level` from `now` on. */
export function still(level: number, now: number): Fade {
  return { from: level, to: level, start: now, seconds: 0 };
}

/**
 * Fade `param` from wherever the previous fade has got to, to `to`, equal power. The curve is laid
 * out as short linear ramps: a ramp can be cancelled at any moment in every browser, a running
 * setValueCurveAtTime cannot. Cancelling drops the param back to its last scheduled value, so the
 * current level is set again first.
 */
export function fadeTo(param: ParamLike, last: Fade, to: number, now: number, seconds: number): Fade {
  const from = gainAt(last, now);
  param.cancelScheduledValues(now);
  param.setValueAtTime(from, now);

  if (seconds <= 0) {
    param.setValueAtTime(to, now);
    return still(to, now);
  }

  const count = Math.min(CURVE_POINTS, Math.max(MIN_POINTS, Math.ceil(seconds * POINTS_PER_S)));
  const points = curve(from, to, count);
  const step = seconds / (count - 1);
  for (let i = 1; i < count; i++) {
    param.linearRampToValueAtTime(points[i]!, now + i * step);
  }
  return { from, to, start: now, seconds };
}

export function dbToGain(db: number): number {
  return 10 ** (db / DB_PER_DECADE);
}
